import Loader from '@gym-atoms/Loader/Loader';
import { getAccessCardHistory, getWalletPassesForMember } from '@gym-graphql/queries';
import Table, { TableProps } from '@gym-molecules/Table/Table';
import { Member } from '@gym-particles/types/Member';
import { AccessCardHistoryResponse, WalletPassHistoryResponse } from '@gym-src/API';
import { API } from 'aws-amplify';
import dayjs from 'dayjs';
import { Card } from 'primereact/card';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

interface AccessCardHistoryTable {
  id: string;
  accessCardUID: string;
  createdDate: string;
  createdTimestamp: number;
  deactivatedDate: string;
  isActive: boolean;
  status: string;
}

interface WalletPassHistoryTable {
  id: string;
  walletPassType: string;
  createdDate: string;
  createdTimestamp: number;
  revokedDate: string;
  isActive: boolean;
  status: string;
}

interface StatusLabels {
  active: string;
  inactive: string;
}

const formatDate = (date?: string | null) => (date ? dayjs(date).format('DD MMM YYYY') : 'N/A');

const formatWalletPassType = (walletPassType?: string | null) =>
  walletPassType === 'GoogleWalletPass'
    ? 'Google wallet pass'
    : walletPassType === 'AppleWalletPass'
    ? 'Apple wallet pass'
    : walletPassType || 'N/A';

const sortByActiveAndNewest = <T extends { isActive: boolean; createdTimestamp: number }>(
  items: T[]
): T[] =>
  [...items].sort(
    (first, second) =>
      Number(second.isActive) - Number(first.isActive) ||
      second.createdTimestamp - first.createdTimestamp
  );

const parseAccessCards = (
  items: Array<AccessCardHistoryResponse | null> | null | undefined,
  statusLabels: StatusLabels
): AccessCardHistoryTable[] =>
  sortByActiveAndNewest(
    (items || []).flatMap((item) =>
      item
        ? [
            {
              id: `${item.id || item.accessCardUID || 'access-card'}-${item.createdDate || ''}`,
              accessCardUID: item.accessCardUID || 'N/A',
              createdDate: formatDate(item.createdDate),
              createdTimestamp: item.createdDate ? dayjs(item.createdDate).valueOf() : 0,
              deactivatedDate: formatDate(item.deactivatedDateTime),
              isActive: item.isActive === true,
              status: item.isActive ? statusLabels.active : statusLabels.inactive
            }
          ]
        : []
    )
  );

const parseWalletPasses = (
  items: Array<WalletPassHistoryResponse | null> | null | undefined,
  statusLabels: StatusLabels
): WalletPassHistoryTable[] =>
  sortByActiveAndNewest(
    (items || []).flatMap((item) =>
      item
        ? [
            {
              id: `${item.id || 'wallet-pass'}-${item.createdDate || ''}`,
              walletPassType: formatWalletPassType(item.walletPassType),
              createdDate: formatDate(item.createdDate),
              createdTimestamp: item.createdDate ? dayjs(item.createdDate).valueOf() : 0,
              revokedDate: formatDate(item.revokedDate),
              isActive: !item.revokedDate,
              status: item.revokedDate ? statusLabels.inactive : statusLabels.active
            }
          ]
        : []
    )
  );

const MemberModalAccessMethods = (props: { member: Member }) => {
  const [accessCardSortField, setAccessCardSortField] = useState<string>();
  const [accessCardSortOrder, setAccessCardSortOrder] = useState<number>();
  const [accessCardOffset, setAccessCardOffset] = useState(0);
  const [accessCardPageSize, setAccessCardPageSize] = useState(10);
  const [walletPassSortField, setWalletPassSortField] = useState<string>();
  const [walletPassSortOrder, setWalletPassSortOrder] = useState<number>();
  const [walletPassOffset, setWalletPassOffset] = useState(0);
  const [walletPassPageSize, setWalletPassPageSize] = useState(10);
  const [accessCards, setAccessCards] = useState<AccessCardHistoryTable[]>([]);
  const [walletPasses, setWalletPasses] = useState<WalletPassHistoryTable[]>([]);
  const [selectedAccessCard, setSelectedAccessCard] = useState<AccessCardHistoryTable>();
  const [selectedWalletPass, setSelectedWalletPass] = useState<WalletPassHistoryTable>();
  const [isDataLoading, setIsDataLoading] = useState(false);
  const { t } = useTranslation();
  const { gymId } = useParams<{ gymId: string }>();

  const getAccessHistory = async () => {
    setIsDataLoading(true);

    try {
      const [accessCardResponse, walletPassResponse] = await Promise.all([
        API.graphql({
          query: getAccessCardHistory,
          variables: {
            memberId: props.member.id,
            siteLocationId: +gymId
          }
        }) as Promise<{
          data: { getAccessCardHistory: Array<AccessCardHistoryResponse | null> | null };
        }>,
        API.graphql({
          query: getWalletPassesForMember,
          variables: {
            memberId: props.member.id
          }
        }) as Promise<{
          data: { getWalletPassesForMember: Array<WalletPassHistoryResponse | null> | null };
        }>
      ]);
      const statusLabels = {
        active: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.ACTIVE'),
        inactive: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.INACTIVE')
      };

      setAccessCards(parseAccessCards(accessCardResponse.data.getAccessCardHistory, statusLabels));
      setWalletPasses(
        parseWalletPasses(walletPassResponse.data.getWalletPassesForMember, statusLabels)
      );
    } catch (error) {
      console.error('Unable to load member access history', error);
    } finally {
      setIsDataLoading(false);
    }
  };

  useEffect(() => {
    getAccessHistory();
  }, [props.member.id, gymId]);

  const accessCardTableProps: TableProps<AccessCardHistoryTable> = {
    exportFileName: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.ACCESS_CARDS_EXPORT_FILE_NAME'),
    setPageSize: setAccessCardPageSize,
    setOffset: setAccessCardOffset,
    pageSize: accessCardPageSize,
    offset: accessCardOffset,
    setSortField: setAccessCardSortField,
    setSortOrder: setAccessCardSortOrder,
    sortField: accessCardSortField,
    sortOrder: accessCardSortOrder,
    totalRecords: accessCards.length,
    data: accessCards,
    lazy: false,
    uniqueFieldName: 'id',
    selectedRow: selectedAccessCard,
    setSelectedRow: setSelectedAccessCard,
    searchPlaceholderText: t(
      'MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.ACCESS_CARDS_SEARCH_FIELD_TEXT'
    ),
    excelBtntext: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.EXPORT_EXCEL_BUTTON_LABEL'),
    headerText: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.ACCESS_CARDS_HEADING'),
    columns: [
      {
        field: 'accessCardUID',
        header: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.HEADER_ACCESS_ID'),
        toggable: false,
        sortable: true
      },
      {
        field: 'createdDate',
        header: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.HEADER_ISSUED_DATE'),
        toggable: true,
        sortable: true
      },
      {
        field: 'deactivatedDate',
        header: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.HEADER_REVOKED_DATE'),
        toggable: true,
        sortable: true
      },
      {
        field: 'status',
        header: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.HEADER_STATUS'),
        toggable: true,
        sortable: true
      }
    ]
  };

  const walletPassTableProps: TableProps<WalletPassHistoryTable> = {
    exportFileName: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.WALLET_PASSES_EXPORT_FILE_NAME'),
    setPageSize: setWalletPassPageSize,
    setOffset: setWalletPassOffset,
    pageSize: walletPassPageSize,
    offset: walletPassOffset,
    setSortField: setWalletPassSortField,
    setSortOrder: setWalletPassSortOrder,
    sortField: walletPassSortField,
    sortOrder: walletPassSortOrder,
    totalRecords: walletPasses.length,
    data: walletPasses,
    lazy: false,
    uniqueFieldName: 'id',
    selectedRow: selectedWalletPass,
    setSelectedRow: setSelectedWalletPass,
    searchPlaceholderText: t(
      'MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.WALLET_PASSES_SEARCH_FIELD_TEXT'
    ),
    excelBtntext: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.EXPORT_EXCEL_BUTTON_LABEL'),
    headerText: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.WALLET_PASSES_HEADING'),
    columns: [
      {
        field: 'walletPassType',
        header: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.HEADER_TYPE'),
        toggable: false,
        sortable: true
      },
      {
        field: 'createdDate',
        header: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.HEADER_ISSUED_DATE'),
        toggable: true,
        sortable: true
      },
      {
        field: 'revokedDate',
        header: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.HEADER_REVOKED_DATE'),
        toggable: true,
        sortable: true
      },
      {
        field: 'status',
        header: t('MEMBER_DETAILS_MODAL.ACCESS_METHODS_TAB.HEADER_STATUS'),
        toggable: true,
        sortable: true
      }
    ]
  };

  return (
    <Card className="d-flex flex-column h-100 detailed-modal__content-col">
      {isDataLoading ? (
        <Loader shape="table" />
      ) : (
        <>
          <Table {...accessCardTableProps} />
          <div className="mt-5">
            <Table {...walletPassTableProps} />
          </div>
        </>
      )}
    </Card>
  );
};

export default MemberModalAccessMethods;
